/**
 * Session-local cash-planning overlays for the Finances forecast.
 *
 * Applies planned purchases, extra payments, and timing shifts on top of a
 * live {@link CashFlowProjection} without writing bills, overhead, or Sequence.
 */

import type {
  CashFlowProjection,
  CashFlowProjectionItem,
  CashFlowProjectionMonth,
} from './cashFlowProjection';
import { toMonthKey } from './financesForecast';
import {
  expandSupplierBillPayments,
  supplierBillPaidAmount,
  type SupplierBill,
} from './supplierBills';

export type ScenarioSplit = 'lump' | 'fifty_fifty' | 'deposit_net' | 'custom';

export interface ScenarioCustomSlice {
  id: string;
  /** Absolute dollars. Used when set; otherwise `percent` of the outflow total. */
  amount?: number;
  percent?: number;
  date: Date;
  label?: string;
}

export interface ScenarioOutflowAttachment {
  id: string;
  name: string;
  storagePath: string;
  contentType?: string;
  size?: number;
  url?: string;
}

export interface ScenarioOutflow {
  id: string;
  type: 'purchase' | 'payment';
  enabled: boolean;
  label: string;
  vendorName: string;
  amount: number;
  /** Lump / first-payment date. */
  date: Date;
  split: ScenarioSplit;
  /** Days after the first payment for the remainder (`deposit_net` / 50-50). */
  netDays: number;
  /** First-slice percent for `deposit_net` (default 50). */
  depositPercent: number;
  customSlices: ScenarioCustomSlice[];
  /** When set, this extra payment is tagged against a live AP row. */
  againstItemKey?: string;
  /**
   * Live supplier bill this CapEx line stands in for. Amount, vendor, and
   * remaining unpaid installments come from that bill — not leftover budget.
   */
  linkedBillId?: string;
  /** Quotes, photos, and PDFs attached to this CapEx line. */
  attachments?: ScenarioOutflowAttachment[];
}

export interface ScenarioTimingShift {
  id: string;
  type: 'shift';
  enabled: boolean;
  itemKey: string;
  /** Positive = later, negative = earlier. */
  shiftDays: number;
  /** Move this live AP row to today. Not saved on a CapEx plan. */
  payNow?: boolean;
}

/** Named CapEx job (facility, shop expansion) that groups line items. */
export interface ScenarioProject {
  id: string;
  type: 'project';
  enabled: boolean;
  label: string;
  items: ScenarioOutflow[];
}

export type ScenarioLine = ScenarioOutflow | ScenarioTimingShift | ScenarioProject;

export interface ScenarioSlice {
  amount: number;
  dueDate: Date | null;
  label: string;
  vendorName: string;
  outflowId: string;
  projectId?: string;
  itemLabel?: string;
  billId?: string;
  taskId?: string;
  estimated?: boolean;
}

export interface ScenarioMonthImpact {
  monthKey: string;
  label: string;
  baselineEnding: number;
  scenarioEnding: number;
  delta: number;
  notes: string[];
}

export interface ScenarioSafety {
  baselineLowest: number | null;
  scenarioLowest: number | null;
  baselineLowestMonthKey: string | null;
  scenarioLowestMonthKey: string | null;
  baselineRunway: number | null;
  scenarioRunway: number | null;
  firstRedMonthKey: string | null;
  /** Scenario lowest minus baseline lowest (negative = worse). */
  lowestDelta: number | null;
  /** How far the scenario low sits above zero (negative if already red). */
  headroom: number | null;
}

export interface AppliedCashFlowScenario {
  projection: CashFlowProjection;
  impact: ScenarioMonthImpact[];
  safety: ScenarioSafety;
  hasEffect: boolean;
}

export function newScenarioId(prefix = 'sc'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function projectionItemKey(item: CashFlowProjectionItem): string {
  const due = item.dueDate
    ? `${item.dueDate.getFullYear()}-${item.dueDate.getMonth() + 1}-${item.dueDate.getDate()}`
    : 'none';
  return [
    item.kind,
    item.plannedKind ?? '',
    item.projectLabel,
    item.label,
    String(item.amount),
    due,
    item.billId ?? '',
  ].join('|');
}

export function addCalendarDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

export function createBlankOutflow(
  type: 'purchase' | 'payment',
  now: Date = new Date(),
): ScenarioOutflow {
  return {
    id: newScenarioId(type === 'purchase' ? 'buy' : 'pay'),
    type,
    enabled: true,
    label: type === 'purchase' ? 'Planned purchase' : 'Extra payment',
    vendorName: '',
    amount: 0,
    date: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    split: 'lump',
    netDays: 30,
    depositPercent: 50,
    customSlices: [],
  };
}

export function createTimingShift(itemKey: string, shiftDays = 7): ScenarioTimingShift {
  return {
    id: newScenarioId('shift'),
    type: 'shift',
    enabled: true,
    itemKey,
    shiftDays,
  };
}

function startOfCalendarDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function calendarDaysBetween(from: Date, to: Date): number {
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((end - start) / 86_400_000);
}

/**
 * Session-only shifts that pull selected future AP rows onto today.
 * Keys that are already due, overdue, or missing are ignored.
 */
export function payNowShiftLines(
  projection: CashFlowProjection | null | undefined,
  keys: Iterable<string>,
  today: Date,
): ScenarioTimingShift[] {
  const wanted = new Set(keys);
  if (!projection || wanted.size === 0) return [];
  const todayStart = startOfCalendarDay(today);
  const lines: ScenarioTimingShift[] = [];
  for (const month of projection.months) {
    for (const item of month.items) {
      if (item.scenario || item.kind !== 'ap' || !item.dueDate) continue;
      const itemKey = projectionItemKey(item);
      if (!wanted.has(itemKey)) continue;
      const shiftDays = calendarDaysBetween(startOfCalendarDay(item.dueDate), todayStart);
      if (shiftDays >= 0) continue;
      lines.push({
        id: `paynow:${itemKey}`,
        type: 'shift',
        enabled: true,
        itemKey,
        shiftDays,
        payNow: true,
      });
    }
  }
  return lines;
}

export function createBlankProject(now: Date = new Date()): ScenarioProject {
  return {
    id: newScenarioId('proj'),
    type: 'project',
    enabled: true,
    label: 'Shop expansion',
    items: [createBlankOutflow('purchase', now)],
  };
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function outflowStartDate(raw: Date | string): Date {
  if (typeof raw === 'string') {
    const [y, m, d] = raw.split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  return new Date();
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function clampMonthKey(key: string, first: string, last: string): string {
  if (key < first) return first;
  if (key > last) return last;
  return key;
}

function roundMoney(n: number): number {
  return Math.round(n);
}

export interface ExpandOutflowContext {
  projectId?: string;
  projectLabel?: string;
}

/** Expand one planned outflow into cash-out slices. */
export function expandScenarioOutflow(
  line: ScenarioOutflow,
  ctx?: ExpandOutflowContext,
): ScenarioSlice[] {
  if (!line.enabled) return [];
  const total = roundMoney(Number(line.amount) || 0);
  if (total <= 0) return [];
  const vendor = line.vendorName.trim() || (line.type === 'purchase' ? 'Planned purchase' : 'Extra payment');
  const first = outflowStartDate(line.date);
  const netDays = Number.isFinite(line.netDays) ? Math.max(0, Math.round(line.netDays)) : 30;

  const slice = (amount: number, dueDate: Date, label: string): ScenarioSlice => ({
    amount: roundMoney(amount),
    dueDate,
    label: ctx?.projectLabel ? `${ctx.projectLabel} · ${label}` : label,
    vendorName: vendor,
    outflowId: line.id,
    projectId: ctx?.projectId,
    itemLabel: line.label,
  });

  if (line.split === 'lump') {
    return [slice(total, first, line.label || 'Lump sum')];
  }

  if (line.split === 'fifty_fifty') {
    const firstAmt = Math.round(total / 2);
    const rest = total - firstAmt;
    return [
      slice(firstAmt, first, `${line.label} · 50%`),
      slice(rest, addCalendarDays(first, netDays || 30), `${line.label} · 50% balance`),
    ].filter((s) => s.amount > 0);
  }

  if (line.split === 'deposit_net') {
    const pct = Math.min(100, Math.max(0, Number(line.depositPercent) || 50));
    const deposit = Math.round((total * pct) / 100);
    const rest = total - deposit;
    const out: ScenarioSlice[] = [];
    if (deposit > 0) out.push(slice(deposit, first, `${line.label} · ${Math.round(pct)}% deposit`));
    if (rest > 0) {
      out.push(slice(rest, addCalendarDays(first, netDays || 30), `${line.label} · balance`));
    }
    return out;
  }

  const custom = line.customSlices ?? [];
  if (custom.length === 0) return [slice(total, first, line.label || 'Custom')];

  const out: ScenarioSlice[] = [];
  let allocated = 0;
  custom.forEach((c, idx) => {
    const isLast = idx === custom.length - 1;
    let amt: number;
    if (c.amount != null && Number.isFinite(c.amount) && c.amount > 0) {
      amt = isLast ? total - allocated : Math.min(total - allocated, roundMoney(c.amount));
    } else {
      const pct = Number(c.percent) || 0;
      amt = isLast ? total - allocated : Math.round((total * pct) / 100);
    }
    if (amt <= 0) return;
    allocated += amt;
    const due = c.date
      ? new Date(c.date.getFullYear(), c.date.getMonth(), c.date.getDate())
      : first;
    const phaseLabel = c.label
      ? (ctx?.projectLabel && line.label ? `${line.label} · ${c.label}` : c.label)
      : `${line.label} · payment ${idx + 1}`;
    out.push(slice(amt, due, phaseLabel));
  });
  return out;
}

/** Flatten an enabled project’s line items through {@link expandScenarioOutflow}. */
export function expandScenarioProject(project: ScenarioProject): ScenarioSlice[] {
  if (!project.enabled) return [];
  const ctx: ExpandOutflowContext = { projectId: project.id, projectLabel: project.label };
  return project.items.flatMap((item) => expandScenarioOutflow(item, ctx));
}

export function expandScenarioLine(line: ScenarioLine): ScenarioSlice[] {
  if (line.type === 'shift') return [];
  if (line.type === 'project') return expandScenarioProject(line);
  return expandScenarioOutflow(line);
}

/** Non-void supplier bills — open or paid — already represent cash that left or will leave. */
export function coveredCapexBillIds(
  bills: Array<Pick<SupplierBill, 'id' | 'status'>>,
): Set<string> {
  const ids = new Set<string>();
  for (const bill of bills) {
    if (bill.id && bill.status !== 'void') ids.add(bill.id);
  }
  return ids;
}

export function supplierBillsById(bills: SupplierBill[] | undefined): Map<string, SupplierBill> {
  const map = new Map<string, SupplierBill>();
  for (const bill of bills ?? []) {
    if (bill.id) map.set(bill.id, bill);
  }
  return map;
}

export function capexLinkedBill(
  line: Pick<ScenarioOutflow, 'linkedBillId'>,
  billsById?: Map<string, SupplierBill>,
): SupplierBill | undefined {
  const id = line.linkedBillId?.trim();
  if (!id || !billsById) return undefined;
  return billsById.get(id);
}

export function isCapexOutflowCoveredByLiveBill(
  line: Pick<ScenarioOutflow, 'linkedBillId'>,
  coveredBillIds?: Set<string>,
): boolean {
  const id = line.linkedBillId?.trim();
  return !!id && !!coveredBillIds?.has(id);
}

/** Remaining unpaid on the linked bill — that is the plan line total. */
export function capexOutflowLineAmount(
  line: Pick<ScenarioOutflow, 'amount' | 'linkedBillId'>,
  bill?: SupplierBill,
): number {
  const planned = roundMoney(Number(line.amount) || 0);
  if (!bill || bill.status === 'void') return planned;
  if (bill.status === 'paid') return 0;
  return Math.max(0, roundMoney(Number(bill.amount) || 0) - supplierBillPaidAmount(bill));
}

/** @deprecated leftover planned-minus-bill is no longer added; remaining bill slices replace AP. */
export function capexOutflowCashAmount(
  line: Pick<ScenarioOutflow, 'amount' | 'linkedBillId'>,
  bill: SupplierBill | undefined,
  coveredBillIds?: Set<string>,
): number {
  if (bill && bill.status !== 'void') return capexOutflowLineAmount(line, bill);
  if (isCapexOutflowCoveredByLiveBill(line, coveredBillIds)) return 0;
  return roundMoney(Number(line.amount) || 0);
}

function calendarDate(raw: Date | null | undefined, fallback: Date): Date {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
}

function sameCalendarDay(a?: Date, b?: Date): boolean {
  if (!a || !b) return !a && !b;
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/** Copy the live bill onto a CapEx line: amount, vendor, and simple 50/50 terms. */
export function applyCapexBillToOutflow(
  bill: SupplierBill,
  line: Pick<ScenarioOutflow, 'id' | 'type' | 'enabled' | 'label'>,
  options?: { now?: Date; taskEndDateById?: Map<string, Date> },
): Partial<ScenarioOutflow> {
  const now = options?.now ?? new Date();
  const fallback = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const slices = expandSupplierBillPayments(bill, { taskEndDateById: options?.taskEndDateById });
  const firstDated = slices.find((slice) => slice.dueDate)?.dueDate ?? bill.dueDate;
  const firstDue = calendarDate(firstDated, fallback);
  const terms = bill.paymentTerms?.installments ?? [];
  const patch: Partial<ScenarioOutflow> = {
    linkedBillId: bill.id,
    amount: roundMoney(Number(bill.amount) || 0),
    vendorName: bill.vendorName.trim(),
    date: firstDue,
  };
  const evenFifty =
    terms.length === 2
    && Math.round(terms[0].percent) === 50
    && Math.round(terms[1].percent) === 50
    && terms[0].trigger === 'on_approval'
    && terms[1].trigger === 'on_approval';
  if (evenFifty) {
    patch.split = 'fifty_fifty';
    patch.netDays = Number.isFinite(terms[1].netDays) ? Math.max(0, Math.round(terms[1].netDays || 0)) : 30;
    patch.depositPercent = 50;
    patch.customSlices = [];
    return patch;
  }
  // Task-complete / mixed terms stay on the live bill — do not snapshot dates.
  patch.split = 'lump';
  patch.customSlices = [];
  return patch;
}

/** Keep a linked CapEx line aligned with the bill; same reference when already in sync. */
export function syncCapexOutflowToLinkedBill(
  item: ScenarioOutflow,
  bill: SupplierBill | undefined,
  taskEndDateById?: Map<string, Date>,
): ScenarioOutflow {
  if (!bill || bill.status === 'void') return item;
  const patch = applyCapexBillToOutflow(bill, item, { taskEndDateById });
  const customSame = (patch.customSlices ?? []).length === (item.customSlices ?? []).length
    && (patch.customSlices ?? []).every((slice, i) => {
      const cur = item.customSlices[i];
      return !!cur
        && (slice.amount ?? 0) === (cur.amount ?? 0)
        && (slice.label ?? '') === (cur.label ?? '')
        && sameCalendarDay(slice.date, cur.date);
    });
  if (
    item.linkedBillId === patch.linkedBillId
    && item.amount === patch.amount
    && item.vendorName === patch.vendorName
    && item.split === patch.split
    && (patch.netDays === undefined || item.netDays === patch.netDays)
    && sameCalendarDay(item.date, patch.date)
    && (patch.split !== 'custom' || customSame)
  ) {
    return item;
  }
  return { ...item, ...patch };
}

function remainingLinkedBillSlices(
  item: ScenarioOutflow,
  bill: SupplierBill,
  ctx: ExpandOutflowContext | undefined,
  taskEndDateById?: Map<string, Date>,
): ScenarioSlice[] {
  if (bill.status === 'void' || bill.status === 'paid') return [];
  return expandSupplierBillPayments(bill, { taskEndDateById })
    .filter((slice) => {
      if (slice.amount <= 0) return false;
      if (slice.installmentIndex === undefined) return true;
      return !bill.installmentPayments?.[slice.installmentIndex];
    })
    .map((slice) => {
      const inst = slice.installmentIndex !== undefined
        ? bill.paymentTerms?.installments[slice.installmentIndex]
        : undefined;
      const label = ctx?.projectLabel
        ? `${ctx.projectLabel} · ${item.label} · ${slice.label}`
        : `${item.label} · ${slice.label}`;
      return {
        amount: roundMoney(slice.amount),
        dueDate: slice.dueDate ? calendarDate(slice.dueDate, item.date) : null,
        label,
        vendorName: bill.vendorName.trim() || item.vendorName || ctx?.projectLabel || 'Supplier',
        outflowId: item.id,
        projectId: ctx?.projectId,
        itemLabel: item.label,
        billId: bill.id,
        taskId: inst?.taskId,
        estimated: slice.estimated || !slice.dueDate,
      };
    })
    .filter((slice) => slice.amount > 0);
}

export function formatCapexBillOption(
  bill: Pick<SupplierBill, 'amount' | 'vendorName' | 'description' | 'poNumber' | 'status'>,
  projectName?: string,
): string {
  const money = `$${Math.round(Number(bill.amount) || 0).toLocaleString('en-US')}`;
  const vendor = bill.vendorName?.trim();
  const desc = bill.description?.trim();
  const parts = [
    money,
    vendor,
    bill.poNumber?.trim(),
    desc && desc.toLowerCase() !== vendor?.toLowerCase() ? desc : '',
    projectName?.trim(),
    bill.status === 'paid' ? 'Paid' : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

export function sortCapexLinkableBills(
  bills: SupplierBill[],
  hint?: { vendorName?: string; amount?: number },
): SupplierBill[] {
  const list = bills.filter((bill) => bill.id && bill.status !== 'void');
  const vendor = (hint?.vendorName || '').trim().toLowerCase();
  const amount = Number(hint?.amount) || 0;
  const score = (bill: SupplierBill): number => {
    let next = 0;
    const name = bill.vendorName.trim().toLowerCase();
    if (vendor && name && (name.includes(vendor) || vendor.includes(name))) next += 2;
    if (amount > 0) {
      const billAmt = Number(bill.amount) || 0;
      if (billAmt > 0 && Math.abs(billAmt - amount) / amount <= 0.15) next += 1;
    }
    return next;
  };
  return [...list].sort((a, b) => {
    const byScore = score(b) - score(a);
    if (byScore !== 0) return byScore;
    const at = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const bt = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return bt - at;
  });
}

function expandScenarioLineForCash(
  line: ScenarioLine,
  options?: {
    coveredBillIds?: Set<string>;
    billsById?: Map<string, SupplierBill>;
    taskEndDateById?: Map<string, Date>;
  },
): ScenarioSlice[] {
  if (line.type === 'shift') return [];
  const cashOutflow = (item: ScenarioOutflow, ctx?: ExpandOutflowContext): ScenarioSlice[] => {
    const bill = capexLinkedBill(item, options?.billsById);
    if (bill) {
      if (bill.status === 'void') return expandScenarioOutflow(item, ctx);
      return remainingLinkedBillSlices(item, bill, ctx, options?.taskEndDateById);
    }
    if (isCapexOutflowCoveredByLiveBill(item, options?.coveredBillIds)) return [];
    return expandScenarioOutflow(item, ctx);
  };
  if (line.type === 'project') {
    if (!line.enabled) return [];
    const ctx: ExpandOutflowContext = { projectId: line.id, projectLabel: line.label };
    return line.items.flatMap((item) => cashOutflow(item, ctx));
  }
  return cashOutflow(line);
}

export function projectRolledTotal(
  project: ScenarioProject,
  billsById?: Map<string, SupplierBill>,
): number {
  return project.items.reduce((sum, item) => {
    if (!item.enabled) return sum;
    return sum + capexOutflowLineAmount(item, capexLinkedBill(item, billsById));
  }, 0);
}

export function projectCashDates(
  project: ScenarioProject,
  options?: { billsById?: Map<string, SupplierBill>; taskEndDateById?: Map<string, Date> },
): { first: Date | null; last: Date | null } {
  const slices = expandScenarioLineForCash(
    { ...project, enabled: true },
    { billsById: options?.billsById, taskEndDateById: options?.taskEndDateById },
  );
  const dated = slices.filter((s): s is ScenarioSlice & { dueDate: Date } => !!s.dueDate);
  if (dated.length === 0) return { first: null, last: null };
  let min = dated[0].dueDate.getTime();
  let max = min;
  for (const s of dated) {
    const t = s.dueDate.getTime();
    if (t < min) min = t;
    if (t > max) max = t;
  }
  return { first: new Date(min), last: new Date(max) };
}

function cloneMonths(baseline: CashFlowProjection): CashFlowProjectionMonth[] {
  return baseline.months.map((m) => ({
    ...m,
    items: m.items.map((item) => ({ ...item })),
  }));
}

function findItem(
  months: CashFlowProjectionMonth[],
  itemKey: string,
): { month: CashFlowProjectionMonth; index: number; item: CashFlowProjectionItem } | null {
  for (const month of months) {
    const index = month.items.findIndex((item) => !item.scenario && projectionItemKey(item) === itemKey);
    if (index >= 0) return { month, index, item: month.items[index] };
  }
  return null;
}

function recalcProjection(
  months: CashFlowProjectionMonth[],
  startingBalance: number,
  arBeyondWindow: number,
): CashFlowProjection {
  let balance = roundMoney(startingBalance);
  let lowestBalance: number | null = null;
  let lowestBalanceMonthKey: string | null = null;
  let runwayMonths: number | null = null;
  const totals = { arIn: 0, apOut: 0, burn: 0, plannedOut: 0, jobOut: 0 };

  const next = months.map((m, i) => {
    const arIn = roundMoney(
      m.items.filter((item) => item.kind === 'ar').reduce((s, item) => s + item.amount, 0),
    );
    const apOut = roundMoney(
      m.items.filter((item) => item.kind === 'ap' && !item.withinBurnBudget)
        .reduce((s, item) => s + item.amount, 0),
    );
    const plannedOut = roundMoney(
      m.items.filter((item) => item.kind === 'planned').reduce((s, item) => s + item.amount, 0),
    );
    const jobOut = roundMoney(
      m.items.filter((item) => item.kind === 'job').reduce((s, item) => s + item.amount, 0),
    );
    const burn = roundMoney(m.burn);
    const net = arIn - apOut - burn - plannedOut - jobOut;
    balance += net;
    totals.arIn += arIn;
    totals.apOut += apOut;
    totals.burn += burn;
    totals.plannedOut += plannedOut;
    totals.jobOut += jobOut;
    if (lowestBalance === null || balance < lowestBalance) {
      lowestBalance = balance;
      lowestBalanceMonthKey = m.monthKey;
    }
    if (runwayMonths === null && balance < 0) runwayMonths = i;
    const kindRank = { ar: 0, ap: 1, job: 2, planned: 3 };
    const items = [...m.items].sort((a, b) =>
      a.kind === b.kind ? b.amount - a.amount : kindRank[a.kind] - kindRank[b.kind],
    );
    return {
      ...m,
      arIn,
      apOut,
      burn,
      plannedOut,
      jobOut,
      net,
      endingBalance: balance,
      hasEstimatedAr: items.some((item) => item.kind === 'ar' && item.estimated),
      items,
    };
  });

  return {
    months: next,
    startingBalance: roundMoney(startingBalance),
    lowestBalance,
    lowestBalanceMonthKey,
    runwayMonths,
    arBeyondWindow: roundMoney(arBeyondWindow),
    totals,
  };
}

function buildSafety(baseline: CashFlowProjection, scenario: CashFlowProjection): ScenarioSafety {
  const firstRed = scenario.months.find((m) => m.endingBalance < 0);
  const lowestDelta =
    baseline.lowestBalance != null && scenario.lowestBalance != null
      ? scenario.lowestBalance - baseline.lowestBalance
      : null;
  return {
    baselineLowest: baseline.lowestBalance,
    scenarioLowest: scenario.lowestBalance,
    baselineLowestMonthKey: baseline.lowestBalanceMonthKey,
    scenarioLowestMonthKey: scenario.lowestBalanceMonthKey,
    baselineRunway: baseline.runwayMonths,
    scenarioRunway: scenario.runwayMonths,
    firstRedMonthKey: firstRed?.monthKey ?? null,
    lowestDelta,
    headroom: scenario.lowestBalance,
  };
}

function buildImpact(
  baseline: CashFlowProjection,
  scenario: CashFlowProjection,
): ScenarioMonthImpact[] {
  const byKey = new Map(scenario.months.map((m) => [m.monthKey, m]));
  const out: ScenarioMonthImpact[] = [];
  for (const m of baseline.months) {
    const s = byKey.get(m.monthKey);
    if (!s) continue;
    const notes = s.items
      .filter((item) => item.scenario)
      .map((item) => {
        const sign = item.kind === 'ar' ? '+' : '−';
        return `${monthLabel(s.month)} ${sign}$${Math.abs(item.amount).toLocaleString('en-US')} ${item.label}`;
      });
    const delta = s.endingBalance - m.endingBalance;
    if (delta === 0 && notes.length === 0) continue;
    out.push({
      monthKey: m.monthKey,
      label: monthLabel(s.month),
      baselineEnding: m.endingBalance,
      scenarioEnding: s.endingBalance,
      delta,
      notes,
    });
  }
  return out;
}

export function scenarioHasEffect(lines: ScenarioLine[]): boolean {
  return lines.some((line) => {
    if (!line.enabled) return false;
    if (line.type === 'shift') return line.shiftDays !== 0 && !!line.itemKey;
    if (line.type === 'project') {
      return line.items.some((item) => item.enabled && (Number(item.amount) || 0) > 0);
    }
    return (Number(line.amount) || 0) > 0;
  });
}

/** Replay the live projection with sandbox purchases, payments, and date shifts. */
export function applyCashFlowScenario(
  baseline: CashFlowProjection,
  lines: ScenarioLine[],
  options?: {
    coveredBillIds?: Set<string>;
    bills?: SupplierBill[];
    taskEndDateById?: Map<string, Date>;
  },
): AppliedCashFlowScenario {
  const months = cloneMonths(baseline);
  if (months.length === 0) {
    return {
      projection: baseline,
      impact: [],
      safety: buildSafety(baseline, baseline),
      hasEffect: false,
    };
  }

  const firstKey = months[0].monthKey;
  const lastKey = months[months.length - 1].monthKey;
  const billsById = supplierBillsById(options?.bills);
  // Live unpaid bills stay on the forecast until paid or voided. CapEx
  // linked to those bills must not strip them or restyle them as plan rows.
  const alreadyRepresentedBillIds = new Set<string>();
  for (const month of months) {
    for (const item of month.items) {
      const id = item.billId?.trim();
      if (id) alreadyRepresentedBillIds.add(id);
    }
  }
  const monthByKey = new Map(months.map((m) => [m.monthKey, m]));

  const putInMonth = (key: string, item: CashFlowProjectionItem) => {
    const clamped = clampMonthKey(key, firstKey, lastKey);
    monthByKey.get(clamped)?.items.push(item);
  };

  for (const line of lines) {
    if (line.type !== 'shift' || !line.enabled || !line.itemKey || line.shiftDays === 0) continue;
    const found = findItem(months, line.itemKey);
    if (!found) continue;
    const [removed] = found.month.items.splice(found.index, 1);
    const from = removed.dueDate ?? found.month.month;
    const nextDue = addCalendarDays(from, line.shiftDays);
    const wasDue = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const dir = line.shiftDays > 0 ? `+${line.shiftDays}d` : `${line.shiftDays}d`;
    putInMonth(toMonthKey(startOfMonth(nextDue)), {
      ...removed,
      dueDate: nextDue,
      scenario: true,
      payNowKey: line.payNow ? line.itemKey : undefined,
      anchor: line.payNow
        ? [removed.anchor, `Paying today · was due ${wasDue}`].filter(Boolean).join(' · ')
        : [removed.anchor, `shifted ${dir} (sandbox)`].filter(Boolean).join(' · '),
    });
  }

  for (const line of lines) {
    if (line.type === 'shift') continue;
    const againstKey = line.type === 'payment' || line.type === 'purchase' ? line.againstItemKey : undefined;
    const addedBillIds = new Set<string>();
    for (const slice of expandScenarioLineForCash(line, {
      coveredBillIds: options?.coveredBillIds,
      billsById,
      taskEndDateById: options?.taskEndDateById,
    })) {
      if (slice.billId && alreadyRepresentedBillIds.has(slice.billId)) continue;
      if (slice.billId) addedBillIds.add(slice.billId);
      putInMonth(slice.dueDate ? toMonthKey(startOfMonth(slice.dueDate)) : firstKey, {
        kind: againstKey ? 'ap' : 'planned',
        plannedKind: againstKey ? undefined : 'other',
        projectLabel: slice.vendorName,
        label: slice.label,
        amount: slice.amount,
        dueDate: slice.dueDate,
        estimated: !!slice.estimated || !slice.dueDate,
        scenario: true,
        billId: slice.billId,
        taskId: slice.taskId,
        anchor: againstKey
          ? 'Extra payment on a live bill (sandbox)'
          : slice.taskId
            ? 'CapEx · tracks schedule'
            : slice.projectId
              ? 'CapEx project'
              : 'CapEx plan',
      });
    }
    addedBillIds.forEach((id) => alreadyRepresentedBillIds.add(id));
  }

  const projection = recalcProjection(months, baseline.startingBalance, baseline.arBeyondWindow);
  const hasEffect = scenarioHasEffect(lines);
  return {
    projection,
    impact: hasEffect ? buildImpact(baseline, projection) : [],
    safety: buildSafety(baseline, projection),
    hasEffect,
  };
}

export function listShiftableItems(projection: CashFlowProjection): CashFlowProjectionItem[] {
  return projection.months.flatMap((m) =>
    m.items.filter((item) => !item.scenario && item.kind !== 'planned' && item.kind !== 'job'),
  );
}

/** Prefix ids so stacked plans cannot collide when concatenated. */
export function prefixScenarioLineIds(lines: ScenarioLine[], prefix: string): ScenarioLine[] {
  const tag = (id: string) => (id.startsWith(`${prefix}::`) ? id : `${prefix}::${id}`);
  return lines.map((line) => {
    if (line.type === 'shift') return { ...line, id: tag(line.id) };
    if (line.type === 'project') {
      return {
        ...line,
        id: tag(line.id),
        items: line.items.map((item) => ({ ...item, id: tag(item.id) })),
      };
    }
    return { ...line, id: tag(line.id) };
  });
}

export function mergeStackedScenarioLines(
  layers: Array<{ id: string; lines: ScenarioLine[] }>,
): ScenarioLine[] {
  return layers.flatMap((layer) => prefixScenarioLineIds(layer.lines, layer.id));
}

export function formatScenarioMoney(n: number): string {
  const abs = Math.abs(Math.round(n));
  const body = `$${abs.toLocaleString('en-US')}`;
  return n < 0 ? `−${body}` : body;
}
